import React, { useState, useEffect, useRef } from 'react';
import api, { workoutPlans } from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from './Layout/DashboardLayout';
import { isAiFailure } from '../utils/aiFailure';
import './AIPersonalTrainer.css';

const WORKOUT_TYPES = [
  { emoji: '🫁', label: 'Peito', key: 'peito', prompt: 'Cria um treino completo de PEITO para hoje. Inclui exercícios, séries, repetições, tempo de descanso e dicas de execução.' },
  { emoji: '🔙', label: 'Costas', key: 'costas', prompt: 'Cria um treino completo de COSTAS para hoje. Inclui exercícios, séries, repetições, tempo de descanso e dicas de execução.' },
  { emoji: '💪', label: 'Braços', key: 'bracos', prompt: 'Cria um treino completo de BRAÇOS (bíceps e tríceps) para hoje. Inclui exercícios, séries, repetições, tempo de descanso e dicas de execução.' },
  { emoji: '🦵', label: 'Pernas', key: 'pernas', prompt: 'Cria um treino completo de PERNAS para hoje. Inclui exercícios, séries, repetições, tempo de descanso e dicas de execução.' },
  { emoji: '🏔️', label: 'Ombros', key: 'ombros', prompt: 'Cria um treino completo de OMBROS para hoje. Inclui exercícios, séries, repetições, tempo de descanso e dicas de execução.' },
  { emoji: '🔥', label: 'Core', key: 'core', prompt: 'Cria um treino completo de CORE e ABDOMINAIS para hoje. Inclui exercícios, séries, repetições, tempo de descanso e dicas de execução.' },
  { emoji: '❤️', label: 'Cardio', key: 'cardio', prompt: 'Cria um treino de CARDIO para hoje. Inclui tipo de exercício, duração, intensidade e dicas.' },
  { emoji: '💥', label: 'Full Body', key: 'fullbody', prompt: 'Cria um treino FULL BODY completo para hoje. Inclui exercícios para todos os grupos musculares, séries, repetições, tempo de descanso e dicas.' },
  { emoji: '🧘', label: 'Alongamentos', key: 'alongamentos', prompt: 'Cria uma rotina de ALONGAMENTOS e MOBILIDADE completa para hoje. Inclui exercícios, duração de cada alongamento e dicas de execução.' },
];

// Strip markdown bold markers: **text** → text
const stripBold = (s) => s ? s.replace(/\*\*/g, '') : '';

// Format AI message text into structured JSX
const formatMessage = (text) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  const elements = [];
  let currentBlock = [];
  let blockType = null; // 'exercise' | 'detail' | 'text'
  
  const flushBlock = () => {
    if (currentBlock.length === 0) return;
    if (blockType === 'exercise') {
      elements.push(
        <div key={elements.length} className="msg-exercise-block">
          {currentBlock}
        </div>
      );
    } else {
      elements.push(...currentBlock);
    }
    currentBlock = [];
    blockType = null;
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed === '---' || trimmed === '***') {
      flushBlock();
      return;
    }

    // Strip markdown bold for matching, keep raw for display
    const clean = stripBold(trimmed);

    // Markdown exercise header: "**Exercício 1: Name**" or "**Exercício 1 - Name**"
    const mdExMatch = clean.match(/^Exerc[ií]cio\s+(\d+)[:\s-]+(.+)/i);
    // Numbered exercise header: "1. Supino reto" or "**1. Supino reto**"
    const exerciseMatch = clean.match(/^(\d+)\.\s+(.+)/);
    // Sub-detail line: "- Séries: 4" or "- **Séries:** 4" or "• text"
    const detailMatch = trimmed.match(/^[-•]\s+(.+)/);
    // Bold-like patterns: "Séries:", "Repetições:", "Tempo de descanso:", "Dicas:"
    const labelMatch = clean.match(/^[-•]?\s*(S[eé]ries|Repeti[çc][oõ]es|Tempo de [Dd]escanso|Dicas?|Notas?|Descanso):\s*(.*)$/i);
    // Standalone bold heading: "**Treino de Peito**" or "**Observações:**"
    const headingMatch = trimmed.match(/^\*\*(.+?)\*\*:?\s*$/);

    if (mdExMatch) {
      flushBlock();
      blockType = 'exercise';
      currentBlock.push(
        <div key={`ex-${i}`} className="msg-exercise-title">
          <span className="msg-exercise-num">{mdExMatch[1]}</span>
          <span className="msg-exercise-name">{mdExMatch[2]}</span>
        </div>
      );
    } else if (exerciseMatch && !labelMatch) {
      flushBlock();
      blockType = 'exercise';
      currentBlock.push(
        <div key={`ex-${i}`} className="msg-exercise-title">
          <span className="msg-exercise-num">{exerciseMatch[1]}</span>
          <span className="msg-exercise-name">{stripBold(exerciseMatch[2])}</span>
        </div>
      );
    } else if (labelMatch) {
      const label = labelMatch[1];
      const value = labelMatch[2] || '';
      const icon = label.toLowerCase().startsWith('sér') ? '🔄' 
        : label.toLowerCase().startsWith('rep') ? '💪' 
        : label.toLowerCase().startsWith('tempo') || label.toLowerCase().startsWith('descanso') ? '⏱️' 
        : label.toLowerCase().startsWith('dica') ? '💡' 
        : label.toLowerCase().startsWith('nota') ? '📝' : '•';
      currentBlock.push(
        <div key={`lbl-${i}`} className="msg-detail-line">
          <span className="msg-detail-icon">{icon}</span>
          <span><strong>{label}:</strong> {value}</span>
        </div>
      );
    } else if (detailMatch) {
      const detailClean = stripBold(detailMatch[1]);
      // Check if the detail itself contains a label like "**Séries:** 3"
      const innerLabel = detailClean.match(/^(S[eé]ries|Repeti[çc][oõ]es|Tempo de [Dd]escanso|Dicas?|Notas?|Descanso):\s*(.*)/i);
      if (innerLabel) {
        const label = innerLabel[1];
        const value = innerLabel[2] || '';
        const icon = label.toLowerCase().startsWith('sér') ? '🔄' 
          : label.toLowerCase().startsWith('rep') ? '💪' 
          : label.toLowerCase().startsWith('tempo') || label.toLowerCase().startsWith('descanso') ? '⏱️' 
          : label.toLowerCase().startsWith('dica') ? '💡' 
          : label.toLowerCase().startsWith('nota') ? '📝' : '•';
        currentBlock.push(
          <div key={`lbl-${i}`} className="msg-detail-line">
            <span className="msg-detail-icon">{icon}</span>
            <span><strong>{label}:</strong> {value}</span>
          </div>
        );
      } else {
        currentBlock.push(
          <div key={`det-${i}`} className="msg-detail-line">
            <span className="msg-detail-icon">•</span>
            <span>{detailClean}</span>
          </div>
        );
      }
    } else if (headingMatch) {
      flushBlock();
      elements.push(
        <div key={`h-${i}`} className="msg-section-heading">
          <strong>{stripBold(headingMatch[1])}</strong>
        </div>
      );
    } else {
      // Regular text line
      if (blockType === 'exercise') {
        currentBlock.push(
          <div key={`txt-${i}`} className="msg-detail-line">
            <span>{clean}</span>
          </div>
        );
      } else {
        flushBlock();
        elements.push(<p key={`p-${i}`} className="msg-text-line">{clean}</p>);
      }
    }
  });
  
  flushBlock();
  return elements;
};

const AIPersonalTrainer = ({ isAdmin = false }) => {
  const { hasPermission } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [hasActivePlan, setHasActivePlan] = useState(false);
  const [lastWorkoutLabel, setLastWorkoutLabel] = useState(null);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadChatHistory();
    loadTokens();
    loadChatSessions();
    checkActivePlan();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkActivePlan = async () => {
    try {
      const response = await workoutPlans.getAll({});
      const plans = response.data || [];
      setHasActivePlan(plans.some(p => p.isActive));
    } catch (error) {
      console.error('Erro ao verificar planos ativos:', error);
    }
  };

  const loadChatHistory = async () => {
    try {
      const response = await api.get('/AIPersonalTrainer/chat');
      const sorted = [...response.data].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setMessages(sorted);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const loadChatSessions = async () => {
    try {
      const response = await api.get('/AIPersonalTrainer/chat-sessions?days=15');
      setChatHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar sessões de chat:', error);
    }
  };

  const loadTokens = async () => {
    try {
      const response = await api.get('/AIPersonalTrainer/tokens');
      setTokens(response.data);
    } catch (error) {
      console.error('Erro ao carregar tokens:', error);
    }
  };

  const sendMessage = async (messageText) => {
    const text = messageText || inputMessage;
    if (!text.trim()) return false;

    if (!messageText) setInputMessage('');
    
    const userMsg = {
      message: text,
      isFromUser: true,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // Create a placeholder AI message for streaming
    const streamingMsg = {
      message: '',
      isFromUser: false,
      createdAt: new Date().toISOString(),
      isStreaming: true
    };
    setMessages(prev => [...prev, streamingMsg]);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/AIPersonalTrainer/chat-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: text })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Erro ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      let buffer = '';
      let isThinking = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.substring(6).trim();
          if (payload === '[DONE]') continue;
          try {
            const parsed = JSON.parse(payload);
            
            // Handle thinking phase indicator
            if (parsed.thinking) {
              isThinking = true;
              setMessages(prev => {
                const updated = [...prev];
                const lastIdx = updated.length - 1;
                if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
                  updated[lastIdx] = { ...updated[lastIdx], isThinking: true, message: '' };
                }
                return updated;
              });
              continue;
            }
            
            if (parsed.token) {
              // First content token: end thinking phase
              if (isThinking) {
                isThinking = false;
              }
              accumulated += parsed.token;
              // Update the streaming message in real-time
              setMessages(prev => {
                const updated = [...prev];
                const lastIdx = updated.length - 1;
                if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
                  updated[lastIdx] = { ...updated[lastIdx], message: accumulated, isThinking: false };
                }
                return updated;
              });
            }
          } catch (e) {
            // skip malformed SSE data
          }
        }
      }

      // Finalize: remove streaming flag, reload history and tokens
      setMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
          updated[lastIdx] = { ...updated[lastIdx], isStreaming: false };
        }
        return updated;
      });

      // Refresh from DB to sync IDs and reload tokens/sessions
      await loadChatHistory();
      await loadChatSessions();
      await loadTokens();
      // Uma resposta vazia ou um aviso de falha da IA não conta como treino: não se oferece para o guardar nos planos.
      return !isAiFailure(accumulated);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error(error.message || 'Erro ao comunicar com a MMIA. Tente novamente.');
      // Remove both the user msg and the failed streaming msg
      setMessages(prev => prev.filter(m => m !== userMsg && !m.isStreaming));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleQuickWorkout = async (workoutType) => {
    setLastWorkoutLabel(workoutType.label);
    setShowSavePrompt(false);
    const success = await sendMessage(workoutType.prompt);
    if (success && hasActivePlan) {
      setShowSavePrompt(true);
    } else if (!success) {
      setLastWorkoutLabel(null);
    }
  };

  const saveToPlan = async () => {
    const lastAiMsg = [...messages].reverse().find(m => !m.isFromUser);
    if (!lastAiMsg) return;
    if (isAiFailure(lastAiMsg.message)) {
      toast.error('Esta resposta é um aviso de falha da IA, não um treino. Peça outra vez quando o serviço voltar.');
      setShowSavePrompt(false);
      return;
    }

    setLoading(true);
    toast.info('A guardar treino no plano...');
    
    try {
      await api.post('/AIPersonalTrainer/approve-plan', {
        planType: 'workout',
        planContent: lastAiMsg.message
      });
      toast.success('✅ Treino guardado nos seus planos com sucesso! (+10 tokens)');
      setShowSavePrompt(false);
      setLastWorkoutLabel(null);
      await loadTokens();
    } catch (error) {
      console.error('Erro ao guardar treino:', error);
      toast.error(error?.response?.data?.message || 'Erro ao guardar treino no plano.');
    } finally {
      setLoading(false);
    }
  };

  const loadChatSession = async (sessionDate) => {
    try {
      const response = await api.get(`/AIPersonalTrainer/chat-session/${sessionDate}`);
      setMessages(response.data);
      setShowHistory(false);
      setShowSavePrompt(false);
      setLastWorkoutLabel(null);
    } catch (error) {
      console.error('Erro ao carregar sessão:', error);
      toast.error('Erro ao carregar sessão de chat.');
    }
  };

  // Se for admin, mostrar AdminAIDashboard
  if (isAdmin) {
    navigate('/admin/ai-dashboard');
    return null;
  }

  return (
    <DashboardLayout userType="client">
      <div className="mmia-container">
        <div className="mmia-page-header">
          <div className="mmia-header-left">
            <img src="/mmia-icon.svg" alt="MMIA" className="mmia-icon-page" />
            <div>
              <h2>MMIA - Personal Trainer do Dia</h2>
              <p className="mmia-subtitle">Olá! Sou a MMIA, a sua Personal Trainer do dia. Escolha um treino rápido ou faça-me uma pergunta!</p>
            </div>
          </div>
          <div className="mmia-header-right">
            {tokens && (
              <div className="tokens-badge">
                <span className="token-icon">🪙</span>
                <span className="token-amount">{tokens.balance} tokens</span>
              </div>
            )}
            <button className="btn-history-toggle" onClick={() => setShowHistory(!showHistory)}>
              📜 Histórico
            </button>
          </div>
        </div>

      {showHistory && (
        <div className="history-overlay" onClick={() => setShowHistory(false)}>
          <div className="history-panel" onClick={(e) => e.stopPropagation()}>
            <div className="history-header">
              <h3>📜 Histórico (15 dias)</h3>
              <button onClick={() => setShowHistory(false)}>×</button>
            </div>
            <div className="history-list">
              {chatHistory.length === 0 ? (
                <p className="no-history">Sem histórico disponível</p>
              ) : (
                chatHistory.map((session, index) => (
                  <div key={index} className="history-item" onClick={() => loadChatSession(session.date)}>
                    <span className="history-date">{new Date(session.date).toLocaleDateString('pt-PT')}</span>
                    <span className="history-count">{session.messageCount} mensagens</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mmia-chat-container">
        <div className="mmia-messages-area">
          {messages.length === 0 && (
            <div className="welcome-message-box">
              <img src="/mmia-icon.svg" alt="MMIA" className="welcome-icon" />
              <h3>Bem-vindo ao seu Treino do Dia! 💪</h3>
              <p>Escolha um grupo muscular abaixo para um treino rápido, ou faça-me qualquer pergunta sobre treino e fitness!</p>
            </div>
          )}

          {messages.map((msg, index) => (
            <div key={index} className={`chat-msg ${msg.isFromUser ? 'user-msg' : 'ai-msg'}`}>
              {!msg.isFromUser && <img src="/mmia-icon.svg" alt="MMIA" className="chat-avatar" />}
              <div className={`chat-bubble ${!msg.isFromUser ? 'chat-bubble-ai' : ''}`}>
                {msg.isFromUser ? (
                  <p>{msg.message}</p>
                ) : msg.isStreaming && msg.isThinking ? (
                  <div className="msg-formatted thinking-phase">
                    <div className="thinking-indicator">
                      <span className="thinking-brain">🧠</span>
                      <span className="thinking-text">A pensar...</span>
                      <span className="thinking-dots"><span>.</span><span>.</span><span>.</span></span>
                    </div>
                  </div>
                ) : msg.isStreaming ? (
                  <div className="msg-formatted streaming-text">
                    <p className="msg-text-line">{msg.message}<span className="streaming-cursor">▌</span></p>
                  </div>
                ) : (
                  <div className="msg-formatted">{formatMessage(msg.message)}</div>
                )}
                <span className="chat-timestamp">{new Date(msg.createdAt).toLocaleTimeString('pt-PT', {hour: '2-digit', minute: '2-digit'})}</span>
              </div>
            </div>
          ))}

          {loading && !messages.some(m => m.isStreaming) && (
            <div className="chat-msg ai-msg">
              <img src="/mmia-icon.svg" alt="MMIA" className="chat-avatar" />
              <div className="chat-bubble">
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          )}

          {/* Prompt para guardar treino no plano */}
          {showSavePrompt && !loading && hasActivePlan && (
            <div className="save-plan-prompt">
              <div className="save-plan-icon">💾</div>
              <p>Deseja guardar este treino{lastWorkoutLabel ? ` de ${lastWorkoutLabel}` : ''} nos seus planos de treino?</p>
              <div className="save-plan-actions">
                <button className="btn-save-plan" onClick={saveToPlan} disabled={loading}>✅ Sim, guardar</button>
                <button className="btn-dismiss-plan" onClick={() => { setShowSavePrompt(false); setLastWorkoutLabel(null); }}>Não, obrigado</button>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="mmia-input-section">
          {hasPermission('canAccessWorkoutPlans') && (
            <div className="quick-workout-grid">
              {WORKOUT_TYPES.map((type) => (
                <button
                  key={type.key}
                  className="quick-workout-btn"
                  onClick={() => handleQuickWorkout(type)}
                  disabled={loading}
                  title={`Treino de ${type.label}`}
                >
                  <span className="quick-workout-emoji">{type.emoji}</span>
                  <span className="quick-workout-label">{type.label}</span>
                </button>
              ))}
            </div>
          )}
          <div className="mmia-input-group">
            <textarea
              className="mmia-input"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Faça uma pergunta sobre treino, nutrição ou fitness..."
              disabled={loading}
              rows={3}
            />
            <button className="mmia-send-btn" onClick={() => sendMessage()} disabled={loading || !inputMessage.trim()}>
              {loading ? '...' : '➤'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </DashboardLayout>
  );
};

export default AIPersonalTrainer;
